import React, { useState, useCallback, useEffect } from 'react'
import {
  AnimatePresence,
  motion
} from 'framer-motion'

import {
  Swiper,
  SwiperSlide,
} from 'swiper/react'

import { Navigation } from 'swiper'

import useIsMobile from 'hooks/useIsMobile'
import usePaginatedGrid from 'hooks/usePaginatedGrid'
import { Label } from '@/atoms/Typography'
import Button from '@/atoms/Button'

import GridItem from './GridItem'
import PaginationNav from './PaginationNav'

import {
  StyledGrid,
  StyledGridContainer,
  StyledGridNavigation,
  StyledGridTotalItems,
  StyledGridLoadMore,
} from './styles'
import { StyledDefaultSliderNavigationButton } from '../Sliders/styles'
import Icon from '@/atoms/Icon'

const Grid = ({
  list,
  type,
  columns = 3,
  showTotalItemLabel = false,
  totalItems = 0,
  mobileSlider,
  imageRatio,
  postType,
  filter,
  perPage,
  brandId,
  onLoadMore: externalLoadMore,
  ...rest
}) => {
  const isControlled = typeof externalLoadMore === 'function'
  const isMobile = useIsMobile()

  const paginated = usePaginatedGrid({
    initialItems: list,
    totalItems,
    postType,
    brandId,
    filter,
    perPage,
    orderBy: rest?.orderBy || 'date',
    recipesDisplayedFilters: rest?.recipesDisplayedFilters || [],
    cocktailDisplayedFilters: rest?.cocktailDisplayedFilters || [],
  })

  const [controlledLoading, setControlledLoading] = useState(false)
  const [controlledHasMore, setControlledHasMore] = useState(totalItems > (list?.length || 0))

  useEffect(() => {
    if (!isControlled) return
    setControlledHasMore(totalItems > (list?.length || 0))
  }, [isControlled, list, totalItems])

  const handleControlledLoadMore = useCallback(async () => {
    if (controlledLoading || !controlledHasMore) return
    setControlledLoading(true)
    try {
      await externalLoadMore()
    } catch (error) {
      console.error('Error loading more grid items:', error)
    } finally {
      setControlledLoading(false)
    }
  }, [controlledLoading, controlledHasMore, externalLoadMore])

  const items = isControlled ? list : paginated.items
  const loading = isControlled ? controlledLoading : paginated.loading
  const hasMore = isControlled ? controlledHasMore : paginated.hasMore

  return (
    <StyledGridContainer
      showTotalItemLabel={showTotalItemLabel}
      isMobile={isMobile}
      mobileSlider={mobileSlider}
    >
      {
        showTotalItemLabel &&
        <StyledGridTotalItems>
          <Label as="span" typo="labelSmall">Ci sono <strong>{totalItems} risultati</strong></Label>
        </StyledGridTotalItems>
      }

      <AnimatePresence mode='wait'>
        {
          items &&
          !!items.length &&
            <>
              {
                (!!isMobile && mobileSlider)
                  ? (
                    <Swiper
                      modules={[Navigation]}
                      spaceBetween={24}
                      slidesOffsetBefore={24}
                      slidesOffsetAfter={24}
                      slidesPerView={'auto'}
                      navigation={{
                        nextEl: '.slider-button-next',
                        prevEl: '.slider-button-prev',
                      }}
                      aria-label="Slider di contenuti"
                      aria-roledescription="carousel"
                    >
                      {
                        items.map((item, i) => (
                          <SwiperSlide
                            key={item.id}
                            role="group"
                            aria-roledescription="slide"
                            aria-label={`${i + 1} di ${items.length}: ${item.title || 'Contenuto'}`}
                          >
                            <GridItem  item={item} type={type} imageRatio={3}/>
                          </SwiperSlide>
                        ))
                      }

                      <StyledGridNavigation aria-label="Controlli slider">
                        <StyledDefaultSliderNavigationButton
                          className='slider-button-prev'
                          aria-label="Vai al contenuto precedente"
                          type="button"
                        >
                          <Icon name='chevron-left' aria-hidden='true' />
                        </StyledDefaultSliderNavigationButton>
                        <StyledDefaultSliderNavigationButton
                          className='slider-button-next'
                          aria-label="Vai al contenuto successivo"
                          type="button"
                        >
                          <Icon name='chevron-right' aria-hidden='true' />
                        </StyledDefaultSliderNavigationButton>
                      </StyledGridNavigation>
                    </Swiper>
                  )
                  : (
                    <>
                      <StyledGrid
                        as={motion.div}
                        transition={{ staggerChildren: 0.2 }}
                        columns={Number(columns)}
                        aria-live="polite"
                      >
                        {
                          items.map((item) => (
                            <GridItem key={item.id} item={item} type={type} imageRatio={imageRatio} />
                          ))
                        }
                      </StyledGrid>

                      {hasMore && (
                        <StyledGridLoadMore>
                          {isControlled ? (
                            <Button
                              label={loading ? 'Caricamento...' : 'Carica altri'}
                              handleClick={handleControlledLoadMore}
                              disabled={loading}
                              aria-label="Carica altri contenuti"
                            />
                          ) : (
                            <Button
                              href={`${paginated.basePath}?page=${paginated.currentPage + 1}`}
                              prefetch={false}
                              handleClick={(event) => {
                                event.preventDefault()
                                paginated.handleLoadMore()
                              }}
                              label={loading ? 'Caricamento...' : 'Carica altri'}
                              disabled={loading}
                              aria-label="Carica altri contenuti"
                            />
                          )}
                        </StyledGridLoadMore>
                      )}

                      {!isControlled && paginated.totalPages > 1 && (
                        <PaginationNav
                          currentPage={paginated.currentPage}
                          totalPages={paginated.totalPages}
                          basePath={paginated.basePath}
                        />
                      )}
                    </>
                  )
              }

            </>
        }
      </AnimatePresence>
    </StyledGridContainer>
  )
}


export default Grid
